import { InfoGrid, StatusChip } from '@/components/ui/info-grid'
import { formatCurrency, formatDate } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
    getInvoicableDecision,
    INVOICABLE_DECISIONS,
    INVOICE_STATUS,
    INVOICE_STATUS_OPTIONS,
    INVOICE_TYPES,
    SIGNED_DOCUMENT_STATUS,
    TRANSACTION_PAYMENT_METHODS,
} from '../../constants'
import {
    SIGNED_DOCUMENT_TYPES,
    SIGNED_DOCUMENTS,
} from '../../constants/signed-documents'
import { InvoiceDetail } from '../../types'
import InvoicePaymentAmount from './InvoicePaymentAmount'

interface InvoiceInfoSectionProps {
    invoice: InvoiceDetail
    invoiceId: number
}

const InvoiceInfoSection: React.FC<InvoiceInfoSectionProps> = ({
    invoice,
    invoiceId,
}) => {
    const { t } = useTranslation()
    const invoicableDecision = getInvoicableDecision(invoice)

    return (
        <InfoGrid
            data-tour="invoice-info"
            className="bg-white rounded-sm"
            items={[
                {
                    label: t('customer_name'),
                    value: (
                        <Link
                            to={generatePathDatacentral(paths.customerDetail, {
                                id: invoice?.customer.customer_id,
                            })}
                            className="text-primary hover:underline uppercase"
                        >
                            {invoice?.customer?.customer_id} -{' '}
                            {invoice?.customer?.company_name ||
                                invoice?.customer?.customer_name}
                        </Link>
                    ),
                },
                {
                    label: t('created_at'),
                    value: formatDate(invoice?.created_at || null),
                },
                {
                    label: t('invoice_duedate'),
                    value: formatDate(invoice?.invoice_duedate || null),
                },
                {
                    label: t('invoice_total'),
                    value: formatCurrency(invoice?.invoice_total || 0),
                },
                {
                    label: t('invoice_payment_at'),
                    value: formatDate(invoice?.invoice_payment_at),
                },
                {
                    label: t('invoice_payment_method'),
                    value: TRANSACTION_PAYMENT_METHODS(t)?.find(
                        (method) =>
                            method.value === invoice?.invoice_payment_method
                    )?.label,
                },
                {
                    label: t('invoice_status'),
                    value: (
                        <StatusChip
                            value={invoice?.invoice_status}
                            options={INVOICE_STATUS(t)}
                        />
                    ),
                },
                // "Đã duyệt" chưa nói CS đã chốt cần xuất hoá đơn hay chưa: hiện riêng quyết định đó
                {
                    hidden: !invoicableDecision,
                    label: t('invoicable-decision.title', { ns: 'invoice' }),
                    value: (
                        <StatusChip
                            value={invoicableDecision ?? ''}
                            options={INVOICABLE_DECISIONS(t)}
                        />
                    ),
                },
                // Badge trạng thái từng chứng từ ký số (giấy đề nghị, biên bản đối soát)
                ...SIGNED_DOCUMENT_TYPES.map((type) => {
                    const { statusField, i18nKey } = SIGNED_DOCUMENTS[type]
                    const status = invoice?.[statusField]
                    return {
                        // API chưa có field này hoặc hoá đơn nạp tiền thì ẩn
                        hidden:
                            status === undefined ||
                            invoice?.invoice_type === INVOICE_TYPES.Deposit,
                        label: t(`${i18nKey}.title`, { ns: 'invoice' }),
                        value: (
                            <StatusChip
                                // `cancelled` (ký lại chưa tạo được bản mới) hiện như chưa gửi
                                value={
                                    status === 'cancelled' ? '' : (status ?? '')
                                }
                                options={SIGNED_DOCUMENT_STATUS(t)}
                            />
                        ),
                    }
                }),
                {
                    hidden:
                        INVOICE_STATUS_OPTIONS.Approved !==
                        invoice?.invoice_status,
                    label: t('paid'),
                    value: <InvoicePaymentAmount invoiceID={invoiceId} />,
                },
            ]}
        />
    )
}

export default InvoiceInfoSection
